import { useEffect, useMemo, useState } from 'react';
import { PMPartsAndProcedure } from '../../types/pmTypes';
import { filterServiceItems, findExistingCategoryName, isProcedureType, getItemTypeLabel, resourcesServiceItems, sortServiceItemsForListing } from '../../utils/companyResourceUtils';
import {
    listServiceItemsByCompanyId,
    listVendorsByCompanyId,
    deleteServiceItem,
    updateServiceItem,
} from '../../graphql/companyResourceQueries';
import {
    AccordionCard,
    Btn,
    BtnIconOnly,
    DataTable,
    IconAlertCircle,
    IconCheckCircle,
    IconClipboard,
    IconClose,
    IconCopy,
    IconEdit,
    IconPlus,
    IconTrash,
} from '../ui';
import type { AccordionCardItem, DataTableColumn } from '../ui';
import { ServiceItemModal } from './ServiceItemModal';
import { EditHistoryModal } from './EditHistoryModal';
import { DeleteServiceItemModal } from './DeleteServiceItemModal';
import { useCompanyUnitsOfMeasure } from '../../hooks/useCompanyUnitsOfMeasure';
import { displayUnitOfMeasure, isExactCatalogUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import { LOCAL_STORAGE_KEYS } from '../../utils/constants';

interface ServiceItemsTabProps { isAdmin: boolean; companyId: string; }

const getExpandedCategoriesStorageKey = (companyId: string) =>
    `${LOCAL_STORAGE_KEYS.SERVICE_ITEMS_EXPANDED_CATEGORIES}:${companyId}`;

const readStoredExpandedCategories = (companyId: string): string[] => {
    try {
        const raw = localStorage.getItem(getExpandedCategoriesStorageKey(companyId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((id): id is string => typeof id === 'string' && id.length > 0)
            : [];
    } catch {
        return [];
    }
};

const serviceItemCategoryElementId = (category: string): string =>
    `service-items-category-${encodeURIComponent(category)}`;

const writeStoredExpandedCategories = (companyId: string, categoryIds: string[]) => {
    try {
        const key = getExpandedCategoriesStorageKey(companyId);
        if (categoryIds.length === 0) {
            localStorage.removeItem(key);
            return;
        }
        localStorage.setItem(key, JSON.stringify(categoryIds));
    } catch {
        // Ignore quota / private-mode failures; in-memory state still works for the session.
    }
};

export const ServiceItemsTab = ({ isAdmin, companyId }: ServiceItemsTabProps) => {
    const [items, setItems] = useState<PMPartsAndProcedure[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [vendors, setVendors] = useState<any[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingItem, setEditingItem] = useState<PMPartsAndProcedure | null>(null);
    const [copyingItem, setCopyingItem] = useState<PMPartsAndProcedure | null>(null);
    const [addToCategory, setAddToCategory] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    const umExtraValues = useMemo(() => {
        const values = items
            .map((item) => item.unitOfMeasure?.trim())
            .filter((value): value is string => Boolean(value));
        return [...new Set(values)].sort((a, b) => a.localeCompare(b));
    }, [items]);
    const { catalogOptions, refresh } = useCompanyUnitsOfMeasure(companyId, umExtraValues);
    // Category rename state
    const [renamingCategory, setRenamingCategory] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [renameError, setRenameError] = useState('');
    const [renaming, setRenaming] = useState(false);

    // Edit history modal state
    const [historyItem, setHistoryItem] = useState<PMPartsAndProcedure | null>(null);
    const [deletingItem, setDeletingItem] = useState<PMPartsAndProcedure | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [expandedIds, setExpandedIds] = useState<string[]>(() =>
        companyId ? readStoredExpandedCategories(companyId) : [],
    );

    useEffect(() => {
        setExpandedIds(companyId ? readStoredExpandedCategories(companyId) : []);
    }, [companyId]);

    const fetchData = async (options?: { silent?: boolean }) => {
        try {
            if (!options?.silent) setIsLoading(true);
            const [itemsData, vendorsData] = await Promise.all([
                listServiceItemsByCompanyId(companyId), listVendorsByCompanyId(companyId),
            ]);
            setItems(itemsData); setVendors(vendorsData);
        } catch (err) { console.error('Error fetching:', err); setError('Failed to load.'); }
        finally { setIsLoading(false); }
    };
    useEffect(() => { if (companyId) fetchData(); }, [companyId]);

    const serviceItems = useMemo(() => resourcesServiceItems(items), [items]);
    const filtered = useMemo(
        () => filterServiceItems(serviceItems, searchQuery, categoryFilter),
        [serviceItems, searchQuery, categoryFilter],
    );
    const categories = [...new Set(serviceItems.map(i => i.category || 'Uncategorized'))].sort();

    const sortedCategories = useMemo(() => {
        const grouped: Record<string, PMPartsAndProcedure[]> = {};
        for (const item of filtered) {
            const cat = item.category || 'Uncategorized';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(item);
        }
        return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
    }, [filtered]);

    const handleConfirmDelete = async () => {
        if (!deletingItem) return;
        setIsDeleting(true);
        try {
            await deleteServiceItem(deletingItem.id);
            setItems(prev => prev.filter(i => i.id !== deletingItem.id));
            setDeletingItem(null);
        } catch (err) {
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };
    const openAddModal = (categoryName = '') => {
        setEditingItem(null);
        setCopyingItem(null);
        setAddToCategory(categoryName);
        setShowModal(true);
    };

    const handleSave = async (saved?: { category: string }) => {
        const categoryName = saved?.category?.trim() || editingItem?.category || addToCategory || '';
        setShowModal(false);
        setEditingItem(null);
        setCopyingItem(null);
        setAddToCategory('');
        await fetchData({ silent: true });
        await refresh();
        if (!categoryName) return;
        setExpandedIds((prev) => {
            if (prev.includes(categoryName)) return prev;
            const next = [...prev, categoryName];
            if (companyId) writeStoredExpandedCategories(companyId, next);
            return next;
        });
        window.setTimeout(() => {
            document.getElementById(serviceItemCategoryElementId(categoryName))?.scrollIntoView({
                block: 'start',
            });
        }, 0);
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getVendorName = (vendorId?: string | null) => { if (!vendorId) return '\u2014'; const v = vendors.find((vn: any) => vn.id === vendorId); return v?.name || '\u2014'; };

    const getBadgeClass = (type?: string | null) => {
        if (isProcedureType(type)) return 'cr-badge--action';
        return 'cr-badge--consumable';
    };

    const handleCategoryRename = async (oldName: string) => {
        const newName = renameValue.trim();
        if (!newName || newName === oldName) {
            setRenamingCategory(null);
            setRenameError('');
            return;
        }
        const conflict = findExistingCategoryName(categories, newName, oldName);
        if (conflict) {
            setRenameError(`A category named “${conflict}” already exists. Category names must be unique.`);
            return;
        }
        setRenaming(true);
        setRenameError('');
        try {
            const itemsToUpdate = items.filter(i => i.category === oldName);
            await Promise.all(itemsToUpdate.map(item => updateServiceItem({ id: item.id, category: newName })));
            setItems(prev => prev.map(i => i.category === oldName ? { ...i, category: newName } : i));
            setExpandedIds((prev) => {
                const next = prev.map((id) => (id === oldName ? newName : id));
                writeStoredExpandedCategories(companyId, next);
                return next;
            });
            setRenamingCategory(null);
        } catch (err) { console.error('Error renaming category:', err); setError('Failed to rename category.'); }
        finally { setRenaming(false); }
    };

    const hasEditHistory = (item: PMPartsAndProcedure) => {
        if (!item.editHistory) return false;
        try { const h = JSON.parse(item.editHistory); return Array.isArray(h) && h.length > 0; }
        catch { return false; }
    };

    const renderActionButtons = (item: PMPartsAndProcedure) => (
        <div className="table-actions">
            <BtnIconOnly
                size="sm"
                icon={<IconEdit />}
                ariaLabel={`Edit ${item.itemName || 'service item'}`}
                onClick={() => {
                    setAddToCategory('');
                    setCopyingItem(null);
                    setEditingItem(item);
                    setShowModal(true);
                }}
            />
            <BtnIconOnly
                size="sm"
                icon={<IconCopy />}
                ariaLabel={`Copy ${item.itemName || 'service item'}`}
                onClick={() => {
                    setAddToCategory('');
                    setEditingItem(null);
                    setCopyingItem(item);
                    setShowModal(true);
                }}
            />
            {hasEditHistory(item) && (
                <BtnIconOnly
                    size="sm"
                    icon={<IconClipboard />}
                    ariaLabel={`View past edits for ${item.itemName || 'service item'}`}
                    onClick={() => setHistoryItem(item)}
                />
            )}
            <BtnIconOnly
                size="sm"
                variant="danger"
                icon={<IconTrash />}
                ariaLabel={`Delete ${item.itemName || 'service item'}`}
                onClick={() => setDeletingItem(item)}
            />
        </div>
    );

    const renderCategoryTable = (catItems: PMPartsAndProcedure[], categoryName: string) => {
        const hasConsumables = catItems.some((i) => !isProcedureType(i.itemType));
        const isActionsOnly = !hasConsumables;

        const columns: DataTableColumn<PMPartsAndProcedure>[] = [
            {
                id: 'description',
                header: 'Description',
                mobileCardPrimary: true,
                renderCell: (item) => item.itemName || '\u2014',
            },
            {
                id: 'type',
                header: 'Type',
                renderCell: (item) =>
                    item.itemType ? (
                        <span className={`cr-badge ${getBadgeClass(item.itemType)}`}>
                            {getItemTypeLabel(item.itemType)}
                        </span>
                    ) : (
                        '\u2014'
                    ),
            },
            ...(!isActionsOnly
                ? ([
                      {
                          id: 'manufacturer',
                          header: 'Manufacturer',
                          renderCell: (item) => item.manufacturer || '\u2014',
                      },
                      {
                          id: 'partNumber',
                          header: 'Part #',
                          renderCell: (item) => item.partNumber || '\u2014',
                      },
                      {
                          id: 'vendor',
                          header: 'Vendor',
                          renderCell: (item) => getVendorName(item.vendorId),
                      },
                      {
                          id: 'unit',
                          header: 'Unit',
                          renderCell: (item) => {
                              const stored = item.unitOfMeasure?.trim();
                              if (!stored) return '\u2014';
                              const inCatalog =
                                  catalogOptions.length === 0 ||
                                  isExactCatalogUnitOfMeasure(stored, catalogOptions);
                              const label =
                                  displayUnitOfMeasure(
                                      stored,
                                      catalogOptions.length > 0 ? catalogOptions : undefined,
                                  ) || stored;
                              if (inCatalog) return label;
                              return (
                                  <span title="This unit of measure is not in the company catalog. Edit the item to update it.">
                                      {label}{' '}
                                      <span className="cr-um-legacy-hint">Update</span>
                                  </span>
                              );
                          },
                      },
                      {
                          id: 'estCost',
                          header: 'Est. Cost',
                          renderCell: (item) =>
                              item.price != null ? `$${item.price.toFixed(2)}` : '\u2014',
                      },
                  ] satisfies DataTableColumn<PMPartsAndProcedure>[])
                : []),
            {
                id: 'created',
                header: 'Created',
                renderCell: (item) =>
                    item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '\u2014',
            },
            {
                id: 'createdBy',
                header: 'Created By',
                renderCell: (item) => item.createdByUserName || '\u2014',
            },
            ...(isAdmin
                ? ([
                      {
                          id: 'actions',
                          header: 'Actions',
                          headerLabel: 'Actions',
                          align: 'center',
                          thClassName: 'data-table-col-narrow',
                          tdClassName: 'data-table-col-narrow',
                          mobileCardAction: true,
                          mobileHideLabel: true,
                          renderCell: (item) => renderActionButtons(item),
                      },
                  ] satisfies DataTableColumn<PMPartsAndProcedure>[])
                : []),
        ];

        const addItemBtn = isAdmin ? (
            <Btn
                variant="secondary-outlined"
                buttonSize="sm"
                buttonIcon={<IconPlus />}
                buttonText="Add Service Item"
                buttonOnClick={() => openAddModal(categoryName)}
            />
        ) : null;

        return (
            <div className="cr-category-body">
                {catItems.length === 0 ? (
                    <div className="cr-category-empty">
                        <p>No service items in this category yet.</p>
                        {addItemBtn}
                    </div>
                ) : (
                    <>
                        <DataTable
                            className="cr-category-data-table"
                            ariaLabel={`${categoryName} service items`}
                            columns={columns}
                            rows={sortServiceItemsForListing(catItems, vendors)}
                            getRowKey={(item) => item.id}
                        />
                        {addItemBtn ? <div className="cr-category-add">{addItemBtn}</div> : null}
                    </>
                )}
            </div>
        );
    };

    const accordionItems: AccordionCardItem[] = sortedCategories.map(([cat, catItems]) => {
        const visibleItems = catItems.filter((item) => item.itemName?.trim());
        const isRenaming = renamingCategory === cat;
        const countLabel = `${visibleItems.length} ${visibleItems.length === 1 ? 'item' : 'items'}`;

        return {
            id: cat,
            htmlId: serviceItemCategoryElementId(cat),
            className: 'cr-category-accordion-section',
            title: isRenaming ? (
                <span className="cr-category-rename-row">
                    <input
                        className={`cr-category-rename-input${renameError ? ' cr-field-error' : ''}`}
                        value={renameValue}
                        onChange={(e) => {
                            setRenameValue(e.target.value);
                            if (renameError) setRenameError('');
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCategoryRename(cat);
                            if (e.key === 'Escape') {
                                setRenamingCategory(null);
                                setRenameError('');
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                        disabled={renaming}
                        aria-label="Rename category"
                        aria-invalid={Boolean(renameError)}
                        aria-describedby={renameError ? `service-items-rename-error-${cat}` : undefined}
                    />
                    <Btn
                        variant="ghost"
                        buttonSize="sm"
                        buttonClasses="cr-category-rename-action"
                        buttonIcon={<IconCheckCircle />}
                        ariaLabel="Save category name"
                        buttonDisabled={renaming}
                        buttonOnClick={(e) => {
                            e.stopPropagation();
                            handleCategoryRename(cat);
                        }}
                    />
                    <Btn
                        variant="ghost"
                        buttonSize="sm"
                        buttonClasses="cr-category-rename-action"
                        buttonIcon={<IconClose />}
                        ariaLabel="Cancel rename"
                        buttonDisabled={renaming}
                        buttonOnClick={(e) => {
                            e.stopPropagation();
                            setRenamingCategory(null);
                            setRenameError('');
                        }}
                    />
                    {renameError && (
                        <span
                            id={`service-items-rename-error-${cat}`}
                            className="cr-field-error-text cr-category-rename-error"
                            role="alert"
                        >
                            {renameError}
                        </span>
                    )}
                </span>
            ) : (
                cat
            ),
            meta: countLabel,
            actions:
                isAdmin && cat !== 'Uncategorized' && !isRenaming ? (
                    <BtnIconOnly
                        size="sm"
                        fill="opaque"
                        icon={<IconEdit />}
                        ariaLabel={`Rename category ${cat}`}
                        onClick={() => {
                            setRenamingCategory(cat);
                            setRenameValue(cat);
                            setRenameError('');
                        }}
                    />
                ) : undefined,
            children: renderCategoryTable(visibleItems, cat),
        };
    });

    const handleExpandedIdsChange = (ids: string[]) => {
        const visibleIds = new Set(sortedCategories.map(([cat]) => cat));
        const hiddenStillExpanded = expandedIds.filter((id) => !visibleIds.has(id));
        const next = [...hiddenStillExpanded, ...ids];
        setExpandedIds(next);
        if (companyId) writeStoredExpandedCategories(companyId, next);
    };

    if (isLoading) return <div className="cr-empty-state"><p className="cr-empty-state-text">Loading...</p></div>;

    return (
        <div>
            <div className="cr-info-banner">
                <span className="cr-info-banner-icon" aria-hidden="true">
                    <IconAlertCircle />
                </span>
                Service items created here are available as dropdown options in PM configurations, Daily Service forms, and Cost Tracking.
            </div>
            <div className="cr-toolbar">
                <input className="cr-search-input" type="text" placeholder="Search by name, category, part #, or manufacturer..."
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                <select className="cr-filter-select" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                    <option value="">All Categories</option>
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                {isAdmin && (
                    <Btn
                        variant="primary"
                        buttonSize="sm"
                        buttonIcon={<IconPlus />}
                        buttonText="Add Service Item"
                        buttonOnClick={() => openAddModal()}
                    />
                )}
            </div>
            {error && <div className="cr-error-banner">{error}</div>}

            {filtered.length === 0 ? (
                <div className="cr-empty-state">
                    <p className="cr-empty-state-text">{serviceItems.length === 0 ? 'No service items yet. Add your first item to get started.' : 'No items match your filter.'}</p>
                </div>
            ) : (
                <AccordionCard
                    className="cr-category-cards"
                    expandMode="multiple"
                    expandedIds={expandedIds}
                    onExpandedIdsChange={handleExpandedIdsChange}
                    items={accordionItems}
                />
            )}

            {showModal && (
                <ServiceItemModal
                    isOpen={showModal}
                    onClose={() => { setShowModal(false); setEditingItem(null); setCopyingItem(null); setAddToCategory(''); }}
                    onSave={handleSave}
                    editItem={copyingItem ?? editingItem}
                    isCopy={Boolean(copyingItem)}
                    vendors={vendors}
                    categories={categories}
                    allItems={items}
                    companyId={companyId}
                    defaultCategory={addToCategory}
                    onVendorCreated={(vendor) => {
                        setVendors((prev) =>
                            prev.some((item) => item.id === vendor.id) ? prev : [...prev, vendor],
                        );
                    }}
                />
            )}

            {historyItem && <EditHistoryModal isOpen={!!historyItem} onClose={() => setHistoryItem(null)} item={historyItem} />}

            <DeleteServiceItemModal
                item={deletingItem}
                isDeleting={isDeleting}
                onDismiss={() => { if (!isDeleting) setDeletingItem(null); }}
                onConfirmDelete={handleConfirmDelete}
            />
        </div>
    );
};
